/**
 * OS Now Playing metadata published per TTS chunk, matching the Flutter app:
 * `title` = chapter title (falling back to the document title when the chunk
 * isn't chapter-aligned, e.g. doc-pack mode), `artist` = document title, album =
 * "Listening". Native adapters forward this to the lock screen; web ignores it.
 */

import type { AudioItemDetail } from '@listening/api-contract/legacy-client-types';
import type { AudioPoolNowPlayingMetadata } from './pool-player';

const NOW_PLAYING_ALBUM = 'Listening';
// Must be http(s) — native artwork is fetched over the network (no file://).
export const NOW_PLAYING_FALLBACK_ARTWORK_URL =
  'https://www.listening.com/social-og-image.png';

export const chapterTitleForId = (
  documentSummary: Pick<AudioItemDetail, 'chapters'>,
  chapterId: string | undefined,
) =>
  chapterId
    ? documentSummary.chapters.find((c) => c.id === chapterId)?.title
    : undefined;

const httpArtworkUrl = (url: string | null | undefined) => {
  const trimmed = url?.trim();
  if (!trimmed) return undefined;
  return /^https?:\/\//i.test(trimmed) ? trimmed : undefined;
};

export const buildNowPlayingMetadata = (
  documentSummary: Pick<
    AudioItemDetail,
    'title' | 'chapters' | 'cover_img_url'
  >,
  chapterId: string | undefined,
): AudioPoolNowPlayingMetadata => {
  const title =
    chapterTitleForId(documentSummary, chapterId) ??
    documentSummary.title ??
    undefined;
  const artist = documentSummary.title ?? undefined;
  return {
    ...(title === undefined ? {} : { title }),
    ...(artist === undefined ? {} : { artist }),
    albumTitle: NOW_PLAYING_ALBUM,
    artworkUrl:
      httpArtworkUrl(documentSummary.cover_img_url) ??
      NOW_PLAYING_FALLBACK_ARTWORK_URL,
  };
};
